import { Controller } from "@hotwired/stimulus"
import { trackBrowserEvent } from "../lib/browser_event_tracker"

// Accessible tabs for the watch player's supporting content. The controller is
// intentionally data-only: richer recommendation metadata can change the panel
// contents later without changing how the player switches between them.
export default class extends Controller {
  static targets = ["tab", "panel", "relatedCount", "relatedContent"]
  static values = {
    relatedUrl: String,
    relatedLessonId: String,
    videoId: Number,
    videoLessonId: Number,
    playerMode: String,
  }

  connect() {
    this.desiredRelatedLessonId = this.hasRelatedLessonIdValue ? this.relatedLessonIdValue : null
    this.loadedRelatedContextKey = null
    this.pendingRelatedContextKey = null
    this.show(this.selectedTabName() || this.tabTargets[0]?.dataset.playerTab)
    this.loadRelated()
  }

  disconnect() {
    this.relatedRequest?.abort()
  }

  select(event) {
    this.selectUserTab(event.currentTarget.dataset.playerTab, 'click')
  }

  openFromEvent(event) {
    const tabName = event.detail?.tab
    this.selectUserTab(tabName, event.detail?.trigger || 'player_action')
    this.tabTargets.find((tab) => tab.dataset.playerTab === tabName)?.focus()
  }

  refreshRelated(event) {
    const lessonId = event.detail?.lessonId
    if (!lessonId) return

    this.desiredRelatedLessonId = String(lessonId)
    if (this.selectedTabName() === 'related') this.loadRelated()
  }

  retryRelated() {
    this.loadedRelatedContextKey = null
    this.loadRelated()
  }

  async loadRelated() {
    if (!this.hasRelatedUrlValue || !this.hasRelatedContentTarget) return

    const lessonId = this.desiredRelatedLessonId
    const contextKey = lessonId || 'whole-video'
    if (contextKey === this.loadedRelatedContextKey || contextKey === this.pendingRelatedContextKey) return

    this.pendingRelatedContextKey = contextKey

    this.relatedRequest?.abort()
    const request = new AbortController()
    this.relatedRequest = request
    const url = new URL(this.relatedUrlValue, window.location.origin)
    if (lessonId) url.searchParams.set('video_lesson_id', lessonId)

    this.relatedContentTarget.setAttribute('aria-busy', 'true')
    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Related videos request failed: ${response.status}`)

      const template = document.createElement('template')
      template.innerHTML = (await response.text()).trim()
      const content = template.content.firstElementChild
      if (!content) return

      const count = Number.parseInt(content.dataset.relatedCount || '0', 10)
      this.relatedContentTarget.replaceChildren(content)
      this.loadedRelatedContextKey = contextKey
      if (this.hasRelatedCountTarget) {
        this.relatedCountTarget.textContent = ` · ${count}`
        this.relatedCountTarget.hidden = count === 0
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Could not refresh related videos:', error)
        this.showRelatedError()
      }
    } finally {
      if (this.relatedRequest === request) {
        this.pendingRelatedContextKey = null
        this.relatedContentTarget.removeAttribute('aria-busy')
      }
    }
  }

  showRelatedError() {
    const message = document.createElement('p')
    message.className = 'player-empty-state'
    message.textContent = 'Related lessons could not be loaded.'
    const retry = document.createElement('button')
    retry.type = 'button'
    retry.className = 'player-related-retry'
    retry.dataset.action = 'player-tabs#retryRelated'
    retry.textContent = 'Try again'
    const wrapper = document.createElement('div')
    wrapper.className = 'player-related-error'
    wrapper.append(message, retry)
    this.relatedContentTarget.replaceChildren(wrapper)
  }

  keydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()
    const currentIndex = this.tabTargets.indexOf(event.currentTarget)
    let nextIndex

    if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = this.tabTargets.length - 1
    else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabTargets.length
    else nextIndex = (currentIndex - 1 + this.tabTargets.length) % this.tabTargets.length

    const nextTab = this.tabTargets[nextIndex]
    this.selectUserTab(nextTab.dataset.playerTab, 'keyboard')
    nextTab.focus()
  }

  openRelatedLesson(event) {
    const card = event.currentTarget.closest('[data-related-video-id]')
    if (!card) return

    trackBrowserEvent({
      event_class: 'RelatedLessonOpened',
      source_video_id: this.videoIdValue,
      source_video_lesson_id: this.relatedContextLessonId(),
      video_id: Number(card.dataset.relatedVideoId),
      video_lesson_id: this.optionalNumber(card.dataset.relatedVideoLessonId),
      recommendation_position: Number(card.dataset.relatedPosition),
      player_mode: this.playerModeValue,
    }, { urgent: true })
  }

  selectUserTab(name, trigger) {
    if (!name || this.selectedTabName() === name) return

    this.show(name)
    trackBrowserEvent({
      event_class: 'PlayerTabSelected',
      video_id: this.videoIdValue,
      video_lesson_id: this.relatedContextLessonId(),
      tab_name: name,
      player_mode: this.playerModeValue,
      trigger,
    })
  }

  show(name) {
    if (!name) return

    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.playerTab === name
      tab.setAttribute('aria-selected', selected.toString())
      tab.tabIndex = selected ? 0 : -1
    })

    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.playerPanel !== name
    })

    if (name === 'related') this.loadRelated()
  }

  selectedTabName() {
    return this.tabTargets.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.playerTab
  }

  relatedContextLessonId() {
    return this.optionalNumber(this.desiredRelatedLessonId) ??
      (this.hasVideoLessonIdValue ? this.videoLessonIdValue : null)
  }

  optionalNumber(value) {
    if (value === undefined || value === null || value === '') return null

    const number = Number(value)
    return Number.isNaN(number) ? null : number
  }
}
